import { useEffect, useMemo, useState } from "react";
import { api, errText } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Languages, Loader2, Plus, RotateCcw, Search } from "lucide-react";

const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-");

const TermRow = ({ row, onSave, onReset }) => {
  const [fr, setFr] = useState(row.fr);
  useEffect(() => setFr(row.fr), [row.fr]);
  const dirty = fr.trim() && fr.trim() !== row.fr;
  return (
    <div className="grid grid-cols-[1fr_1.4fr_100px] gap-2 items-center py-2 border-b border-slate-100" data-testid={`term-row-${row.kind === "place" ? "place-" : ""}${slug(row.en)}`}>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-[#12234A] truncate capitalize">{row.en}</p>
        <p className="text-[11px] text-slate-400">{row.kind}{row.custom && row.default_fr ? " · edited" : ""}</p>
      </div>
      <Input value={fr} onChange={(e) => setFr(e.target.value)} className="h-9 rounded-lg" data-testid={`term-fr-${row.kind === "place" ? "place-" : ""}${slug(row.en)}`} />
      <div className="flex gap-1">
        <Button size="sm" disabled={!dirty} onClick={() => onSave(row.en, fr, row.kind === "place" ? "place" : "term")} className="h-9 rounded-lg bg-[#12234A]" data-testid={`term-save-${row.kind === "place" ? "place-" : ""}${slug(row.en)}`}>Save</Button>
        {row.custom && (
          <Button size="sm" variant="outline" onClick={() => onReset(row.en, row.kind === "place" ? "place" : "term")} className="h-9 rounded-lg" title={row.default_fr ? "Revert to default" : "Delete"} data-testid={`term-reset-${row.kind === "place" ? "place-" : ""}${slug(row.en)}`}>
            <RotateCcw size={14} />
          </Button>
        )}
      </div>
    </div>
  );
};

export function FlyerTerms() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [nEn, setNEn] = useState("");
  const [nFr, setNFr] = useState("");
  const [nKind, setNKind] = useState("term");
  const [filter, setFilter] = useState("all");
  const load = () => api.get("/admin/flyer-terms").then((r) => setRows(r.data.terms)).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, []);
  const save = async (en, fr, kind) => {
    try { await api.put("/admin/flyer-terms", { en, fr, kind }); toast.success(`Saved “${en}” → “${fr.trim()}”`); load(); return true; }
    catch (e) { toast.error(errText(e)); return false; }
  };
  const reset = async (en, kind) => {
    try { await api.delete(`/admin/flyer-terms/${encodeURIComponent(en)}?kind=${kind}`); toast.success("Translation reset"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const add = async () => { if (await save(nEn, nFr, nKind)) { setNEn(""); setNFr(""); } };
  const shown = useMemo(() => (rows || []).filter((r) => (filter === "all" || (filter === "place") === (r.kind === "place"))
    && (!q || `${r.en} ${r.fr}`.toLowerCase().includes(q.toLowerCase()))), [rows, q, filter]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 p-6 max-w-3xl" data-testid="flyer-terms">
      <div className="flex items-start gap-3 mb-4">
        <span className="h-10 w-10 rounded-xl bg-[#FFF4F0] flex items-center justify-center shrink-0"><Languages size={20} className="text-[#F5551E]" /></span>
        <div>
          <h2 className="font-display font-bold text-lg text-[#12234A]">French flyer translations</h2>
          <p className="text-sm text-slate-500">Species, breeds and place words typed in English are swapped for these on French lost-pet flyers. Matching ignores capitals, except built-in place nouns like "park", which only translate in lowercase so names like "High Park" stay as typed. Anything not listed prints as typed.</p>
        </div>
      </div>
      <div className="mb-4 p-3 rounded-xl bg-slate-50 space-y-2" data-testid="term-add">
      <div className="flex gap-1">
        {[["term", "Species / breed"], ["place", "Place word"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setNKind(k)} data-testid={`term-add-kind-${k}`}
            className={`px-3 h-8 rounded-lg text-xs font-semibold transition-colors ${nKind === k ? "bg-[#12234A] text-white" : "bg-white border border-slate-200 text-slate-500"}`}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-[1fr_1.4fr_auto] gap-2">
        <Input value={nEn} onChange={(e) => setNEn(e.target.value)} placeholder="English (e.g. Labradoodle)" className="h-10 rounded-lg bg-white" data-testid="term-add-en" />
        <Input value={nFr} onChange={(e) => setNFr(e.target.value)} placeholder="Français" className="h-10 rounded-lg bg-white" data-testid="term-add-fr" />
        <Button onClick={add} disabled={!nEn.trim() || !nFr.trim()} className="h-10 rounded-lg bg-[#F5551E] hover:bg-[#e04812] gap-1" data-testid="term-add-btn"><Plus size={15} /> Add</Button>
      </div>
      </div>
      <div className="flex gap-1 mb-2" data-testid="term-filter">
        {[["all", "All"], ["term", "Species & breeds"], ["place", "Place words"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`term-filter-${k}`}
            className={`px-3 h-8 rounded-lg text-xs font-semibold transition-colors ${filter === k ? "bg-[#F5551E] text-white" : "bg-slate-100 text-slate-500 hover:text-[#12234A]"}`}>{l}</button>
        ))}
      </div>
      <div className="relative mb-2">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search terms…" className="h-10 rounded-lg pl-9" data-testid="term-search" />
      </div>
      {!rows ? <Loader2 className="animate-spin text-slate-400 my-6" /> : (
        <div className="max-h-[480px] overflow-y-auto pr-1" data-testid="term-list">
          {shown.map((r) => <TermRow key={`${r.kind}-${r.en}`} row={r} onSave={save} onReset={reset} />)}
          {!shown.length && <p className="text-sm text-slate-400 py-6 text-center">No matching terms</p>}
        </div>
      )}
    </div>
  );
}
